// @ts-nocheck
// Unified boss-warning HUD. One consistent banner + debuff-chip language for
// every boss, fed by the per-boss progress helpers (fx-meteor-nuke.ts) and
// player-state fields. Bosses share the look; only palette/text/type vary.
import { clamp, len } from "../../util";
import { uiFont } from "../../ui-tokens";
import { Game } from "../game-base";

// Named color themes so every banner/chip reads consistently. `u` = urgent.
const WARN_THEMES = {
  fire:   { fill: "rgba(40,16,6,0.84)",  fillU: "rgba(48,12,8,0.88)",  stroke: "#ff8844", strokeU: "#ff5030", title: "#ffd8a0", titleU: "#ffb0a0", sub: "#ffb060", subU: "#ff6a3a", bar: "#ff8844", barU: "#ff4020" },
  plague: { fill: "rgba(20,28,10,0.86)", fillU: "rgba(24,32,8,0.9)",   stroke: "#6a8a30", strokeU: "#a8c860", title: "#c8d878", titleU: "#d8f0a0", sub: "#8aa840", subU: "#a8c860", bar: "#6a8a30", barU: "#a8c860" },
  ice:    { fill: "rgba(16,28,40,0.88)", fillU: "rgba(12,24,36,0.92)", stroke: "#6a9ab8", strokeU: "#a8d4f0", title: "#c8e8ff", titleU: "#e8f4ff", sub: "#8ab8d8", subU: "#a8d4f0", bar: "#6a9ab8", barU: "#a8d4f0" },
  gold:   { fill: "rgba(56,28,6,0.86)",  fillU: "rgba(48,36,8,0.88)",  stroke: "#ff9020", strokeU: "#ffc850", title: "#ffe8a0", titleU: "#ffe8a0", sub: "#ffb040", subU: "#ffd060", bar: "#ff9020", barU: "#ffc850" },
  arcane: { fill: "rgba(28,20,40,0.88)", fillU: "rgba(36,16,48,0.9)",  stroke: "#c8a0ff", strokeU: "#e8b840", title: "#e8d8ff", titleU: "#ffd878", sub: "#c8a0ff", subU: "#ff9060", bar: "#8040a0", barU: "#e8b840" },
  void:   { fill: "rgba(24,8,40,0.9)",   fillU: "rgba(32,12,48,0.92)", stroke: "#9030c0", strokeU: "#c878ff", title: "#e8d0ff", titleU: "#e8d0ff", sub: "#c878ff", subU: "#ff9090", bar: "#9030c0", barU: "#c878ff" },
  red:    { fill: "rgba(32,8,48,0.9)",   fillU: "rgba(40,8,40,0.92)",  stroke: "#ff6060", strokeU: "#ff6060", title: "#ffd0d0", titleU: "#ffd0d0", sub: "#ff9090", subU: "#ff9090", bar: "#ff6060", barU: "#ff6060" },
  green:  { fill: "rgba(24,32,8,0.84)",  fillU: "rgba(24,32,8,0.9)",   stroke: "#a8c860", strokeU: "#c8d040", title: "#d8f0a0", titleU: "#d8f0a0", sub: "#a8c860", subU: "#c8d040", bar: "#a8c860", barU: "#c8d040" },
};

// collectBossWarnings derives the single highest-priority "primary" channel
// telegraph plus any active player-state "chips" (debuff timers / persistent
// arena states). No protocol changes — purely a read over snapshot + helpers.
Game.prototype.collectBossWarnings = function (p) {
  return { primary: this.primaryBossWarning(), chips: this.bossDebuffChips(p) };
};

Game.prototype.primaryBossWarning = function () {
  if (!this.showBossChannelHud?.()) return null;
  const boss = this.players.find(pl => pl.boss && pl.alive);

  const lethal = boss ? this.lethalChannelProgress(boss) : null;
  if (lethal?.active) {
    const plague = lethal.kind === "plague";
    return {
      theme: plague ? "plague" : "fire", icon: plague ? "☠" : "🔥",
      title: plague ? "PLAGUE BURST" : "INFERNO NOVA",
      subtitle: plague
        ? (lethal.urgent ? "STAND IN POISON OR DIE" : "GET IN A POISON POOL NOW")
        : (lethal.urgent ? "GET BEHIND STONE OR DIE" : "HIDE BEHIND STONE NOW"),
      prog: lethal.prog, showBar: true, urgent: lethal.urgent,
    };
  }

  const shatter = this.shatterChannelProgress?.();
  if (shatter?.active) {
    return {
      theme: "ice", icon: "❄", title: "SHATTER",
      subtitle: shatter.urgent ? "KEEP MOVING — FROZEN OR STILL HEROES DIE" : "KEEP MOVING — DON'T STOP",
      prog: shatter.prog, showBar: true, urgent: shatter.urgent,
    };
  }

  const frigidWell = this.frigidWellProgress?.();
  if (frigidWell?.active) {
    const winding = frigidWell.phase === "windup";
    return {
      theme: "gold", icon: "◎", title: "FRIGID WELL",
      subtitle: frigidWell.urgent
        ? "SOAK NOW — 500 TO ALL IF EMPTY"
        : (winding ? "RUN TO CENTER — ALL PLAYERS IN RING" : "STAND IN GOLD RING — ALL PLAYERS"),
      prog: frigidWell.prog, showBar: true, urgent: frigidWell.urgent,
    };
  }

  const essenceCast = this.essenceCastProgress?.();
  if (essenceCast?.active) {
    const p = this.human();
    if (essenceCast.kind === "meridian") {
      const ready = this.meridianPlayerReady?.(p, essenceCast.angle);
      const charging = essenceCast.phase === "windup" || essenceCast.phase === "move";
      let subtitle;
      if (ready) {
        subtitle = "READY — on safe half";
      } else if (essenceCast.urgent) {
        subtitle = "VOID → gold half · LIGHT → purple half";
      } else {
        subtitle = "Attune, then stand on matching half before snap";
      }
      return {
        theme: "gold", icon: "☯",
        title: charging ? "ESSENCE MERIDIAN" : "MERIDIAN LOCKING",
        subtitle,
        prog: essenceCast.prog, showBar: true, urgent: essenceCast.urgent && !ready,
      };
    }
    const needLight = essenceCast.needMark === 1;
    const mark = needLight ? "LIGHT" : "VOID";
    const ward = needLight ? "gold ward (left)" : "void ward (right)";
    const ready = p?.essenceMark === essenceCast.needMark;
    const inTelegraph = essenceCast.phase === "telegraph";
    const inRipples = essenceCast.phase === "ripple";
    const halfLight = essenceCast.beat === 1;
    const theme = needLight ? "gold" : "void";
    const icon = needLight ? "☀" : "🌑";
    let subtitle;
    if (ready && inTelegraph) {
      subtitle = `READY — ${mark} attuned`;
    } else if (inTelegraph) {
      subtitle = essenceCast.urgent ? `Attune ${mark} NOW — ${ward}` : `Attune ${mark} — ${ward}`;
    } else if (ready) {
      subtitle = inRipples
        ? `READY — ${mark} attuned (immune to ${halfLight ? "light" : "void"} ripples)`
        : `READY — ${mark} attuned`;
    } else if (inRipples) {
      subtitle = `${halfLight ? "Light" : "Void"} ripples — attune ${mark} at ${ward}`;
    } else if (essenceCast.urgent) {
      subtitle = `Attune ${mark} NOW — ${ward}`;
    } else {
      subtitle = `5 light + 5 void ripples — attune ${mark} for each half`;
    }
    return {
      theme, icon,
      title: "ESSENCE BLOOM",
      subtitle,
      prog: essenceCast.prog,
      showBar: inTelegraph,
      urgent: inTelegraph && !ready,
    };
  }

  const sweepBoss = this.players.find(pl =>
    pl.boss && pl.alive && (pl.channelSpell === "boss_lighthouse_sweep" || pl.channelSpell === "boss_radiant_sweep"),
  ) || boss;
  const sweepActive = sweepBoss && (
    sweepBoss.channelSpell === "boss_lighthouse_sweep" ||
    sweepBoss.channelSpell === "boss_radiant_sweep" ||
    (this.bossBeams && this.bossBeams.length > 0)
  );
  if (sweepActive) {
    const winding = (sweepBoss.channelSpell === "boss_lighthouse_sweep" || sweepBoss.channelSpell === "boss_radiant_sweep") &&
      sweepBoss.channelProg > 0 && sweepBoss.channelProg < 0.95 && !(this.bossBeams && this.bossBeams.length);
    const radiant = sweepBoss.channelSpell === "boss_radiant_sweep" || (this.bossBeams?.[0]?.spellId === "boss_radiant_sweep");
    return {
      theme: "gold", icon: radiant ? "☀" : "✺", title: radiant ? "RADIANT SWEEP" : "SWEEPING LANCE",
      subtitle: winding ? "BEAM INCOMING — KEEP MOVING" : "STAY OUT OF THE BEAM",
      urgent: !winding,
    };
  }

  const gale = this.howlingGaleProgress?.();
  if (gale?.active) {
    const winding = gale.phase === "windup";
    return {
      theme: "ice", icon: "🌬", title: "HOWLING GALE",
      subtitle: winding ? "BRACE FOR PUSH — DON'T FIGHT THE WIND" : "GALE ACTIVE — STAY OFF THE RIM",
      prog: gale.prog, showBar: winding, urgent: !winding,
    };
  }

  const bloom = this.voidBloomProgress?.();
  if (bloom?.active) {
    const winding = bloom.phase === "windup";
    return {
      theme: "void", icon: "🌸", title: "VOID BLOOM",
      subtitle: winding ? "THE EYE IS CHARGING — FIND AN OPENING" : "WEAVE THROUGH THE GAPS",
      prog: bloom.prog, showBar: winding, urgent: !winding,
    };
  }

  return null;
};

Game.prototype.bossDebuffChips = function (p) {
  if (this.state !== "playing") return [];
  const chips = [];

  if (p?.alive && (p.mindCtrlT || 0) > 0) {
    chips.push({
      theme: "red", icon: "👁", title: "DOMINATED", timer: p.mindCtrlT,
      subtitle: "YOU ARE FIGHTING YOUR ALLIES — survive until it ends",
    });
  }
  if (p?.alive && (p.mindLashT || 0) > 0) {
    chips.push({
      theme: "void", icon: "🧠", title: "MIND LASH", timer: p.mindLashT,
      subtitle: "CONTROLS REVERSED — think opposite of where you want to go",
    });
  }
  if (this.ixtholothCollapseActive?.()) {
    chips.push({
      theme: "void", icon: "🌀", title: "RIFT COLLAPSE",
      subtitle: "the floor is caving in — stay off the edge",
    });
  }
  if (p?.alive && p.contagion) {
    const fx = (this._myEffects || []).find(e => e.id === "contagion");
    const rem = fx ? Math.max(0, fx.rem - (performance.now() - fx.at) / 1000) : null;
    const alliesNear = this.players.filter(pl =>
      pl !== p && pl.alive && !pl.falling && pl.team === p.team && len(pl.x - p.x, pl.y - p.y) <= 140,
    ).length;
    chips.push({
      theme: "green", icon: "🦠", title: "CONTAGION", timer: rem != null ? rem : undefined,
      subtitle: alliesNear > 0 ? `SPREAD OUT NOW — ${alliesNear} ally in spread range` : "Ticks damage — stay spread until it expires",
    });
  }
  return chips;
};

const WARN_RADIUS = 14;
const CHIP_RADIUS = 12;
// Top of the warning banner. Sits below the boss HP header (name + bar + HP text
// + Gorge pips / Mirror Court line) so the telegraph never covers the HP bar.
const WARN_TOP_Y = 120;

// roundPath traces a rounded-rect path (native roundRect where available).
function roundPath(c, x, y, w, h, r) {
  r = Math.min(r, h / 2, w / 2);
  c.beginPath();
  if (c.roundRect) { c.roundRect(x, y, w, h, r); return; }
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}

// cachedVGrad memoizes the vertical depth gradient by height bucket so we don't
// allocate a CanvasGradient every frame (gradients are translation-independent —
// we build them at origin and let the path/transform place them). The light→dark
// stops are constant, so one per height is enough.
const _vGradCache = new Map();
function depthGradient(c, h) {
  const key = Math.round(h);
  let g = _vGradCache.get(key);
  if (!g) {
    g = c.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, "rgba(255,255,255,0.10)");
    g.addColorStop(0.45, "rgba(255,255,255,0)");
    g.addColorStop(1, "rgba(0,0,0,0.32)");
    _vGradCache.set(key, g);
  }
  return g;
}

// drawWarnFrame paints the shared panel chrome: a cheap layered-stroke glow (NOT
// shadowBlur — that raster-blurs the whole fill every frame and tanks the frame
// rate), base fill, a top-light/bottom-shade depth gradient, and a crisp border.
// `glow` 0..1 pulses the halo.
function drawWarnFrame(c, x, y, w, h, r, th, urgent, glow) {
  const fill = urgent ? th.fillU : th.fill;
  const stroke = urgent ? th.strokeU : th.stroke;

  // Faked outer glow: two soft expanding strokes — far cheaper than shadowBlur.
  c.save();
  c.strokeStyle = stroke;
  c.globalAlpha = (urgent ? 0.20 : 0.10) * (0.6 + 0.4 * glow);
  c.lineWidth = urgent ? 7 : 5;
  roundPath(c, x, y, w, h, r);
  c.stroke();
  c.globalAlpha = (urgent ? 0.30 : 0.16) * (0.6 + 0.4 * glow);
  c.lineWidth = urgent ? 3.5 : 2.5;
  roundPath(c, x, y, w, h, r);
  c.stroke();
  c.restore();

  // Base fill + depth gradient (fill the rounded path directly — no clip()).
  c.fillStyle = fill;
  roundPath(c, x, y, w, h, r);
  c.fill();
  c.save();
  c.translate(x, y);
  c.fillStyle = depthGradient(c, h);
  roundPath(c, 0, 0, w, h, r);
  c.fill();
  // top sheen line (inset by r so it stays inside the rounded corners)
  c.fillStyle = "rgba(255,255,255,0.12)";
  c.fillRect(r, 1, w - r * 2, 1);
  c.restore();

  // Border.
  c.lineWidth = urgent ? 2 : 1.25;
  c.strokeStyle = stroke;
  roundPath(c, x + 0.75, y + 0.75, w - 1.5, h - 1.5, r - 0.5);
  c.stroke();
}

// drawWarnBadge paints an icon disc on the left of a panel (no shadowBlur).
function drawWarnBadge(c, cx, cy, r, icon, th, urgent) {
  const stroke = urgent ? th.strokeU : th.stroke;
  c.save();
  c.translate(cx, cy);
  c.beginPath();
  c.arc(0, 0, r, 0, Math.PI * 2);
  const g = c.createRadialGradient(0, -r * 0.3, 1, 0, 0, r);
  g.addColorStop(0, "rgba(255,255,255,0.18)");
  g.addColorStop(1, "rgba(0,0,0,0.25)");
  c.fillStyle = g;
  c.fill();
  c.lineWidth = 1.5;
  c.strokeStyle = stroke;
  c.stroke();
  if (icon) {
    c.textAlign = "center";
    c.textBaseline = "middle";
    c.font = `${Math.round(r * 1.25)}px 'Trebuchet MS', sans-serif`;
    c.fillText(icon, 0, 1);
  }
  c.restore();
}

// drawWarnBar paints a rounded progress track with a gradient fill and a bright
// leading cap (no shadowBlur / clip — fill the rounded fill-path directly).
function drawWarnBar(c, x, y, w, h, prog, th, urgent) {
  c.fillStyle = "rgba(0,0,0,0.5)";
  roundPath(c, x, y, w, h, h / 2);
  c.fill();
  const p = clamp(prog, 0, 1);
  if (p > 0) {
    const fw = Math.max(h, w * p);
    const g = c.createLinearGradient(x, 0, x + w, 0);
    g.addColorStop(0, urgent ? th.barU : th.bar);
    g.addColorStop(1, urgent ? th.strokeU : th.barU);
    c.fillStyle = g;
    roundPath(c, x, y, fw, h, h / 2);
    c.fill();
    c.fillStyle = "rgba(255,255,255,0.9)";
    c.beginPath();
    c.arc(x + fw - h / 2, y + h / 2, h / 2 - 1.5, 0, Math.PI * 2);
    c.fill();
  }
}

// drawBossWarnings renders the primary banner (top center) then a stacked column
// of debuff chips beneath it, all in the shared visual language.
Game.prototype.drawBossWarnings = function (c, warnings) {
  let nextY = WARN_TOP_Y - 10;
  if (warnings.primary) nextY = this.drawPrimaryBossWarning(c, warnings.primary);
  if (warnings.chips.length) this.drawBossDebuffChips(c, warnings.chips, nextY + 10);
};

Game.prototype.drawPrimaryBossWarning = function (c, w) {
  const th = WARN_THEMES[w.theme] || WARN_THEMES.void;
  const u = !!w.urgent;
  const showBar = !!w.showBar && (w.prog ?? -1) >= 0;
  const bw = Math.min(560, this.W * 0.88);
  const padX = 16;
  const bh = showBar ? 84 : 66;
  const bx = this.cx - bw / 2;

  // Entrance: ease-in pop when the warning (by title) first appears.
  if (w.title !== this._primWarnKey) { this._primWarnKey = w.title; this._primWarnT0 = this.nowS; }
  const appear = clamp((this.nowS - (this._primWarnT0 ?? this.nowS)) / 0.22, 0, 1);
  const ease = 1 - Math.pow(1 - appear, 3);
  const by = WARN_TOP_Y - (1 - ease) * 12;
  const glow = u ? 0.5 + 0.5 * Math.sin(this.nowS * 7) : 0.35 + 0.2 * Math.sin(this.nowS * 3);

  c.save();
  c.globalAlpha = 0.25 + 0.75 * ease;
  drawWarnFrame(c, bx, by, bw, bh, WARN_RADIUS, th, u, glow);

  const r = 18;
  const badgeCx = bx + padX + r;
  const badgeCy = by + 34;
  drawWarnBadge(c, badgeCx, badgeCy, r, w.icon, th, u);

  const textX = badgeCx + r + 14;
  c.textAlign = "left";
  c.textBaseline = "alphabetic";
  c.font = uiFont(800, "lg");
  if ("letterSpacing" in c) c.letterSpacing = "1px";
  c.fillStyle = u ? th.titleU : th.title;
  c.fillText(w.title, textX, by + 31);
  if ("letterSpacing" in c) c.letterSpacing = "0px";
  c.font = uiFont(700, "sm");
  c.fillStyle = u ? th.subU : th.sub;
  c.fillText(w.subtitle, textX, by + 52);

  if (showBar) {
    drawWarnBar(c, textX, by + bh - 16, bx + bw - padX - textX, 7, w.prog ?? 0, th, u);
  }
  c.restore();
  return by + bh;
};

Game.prototype.drawBossDebuffChips = function (c, chips, startY) {
  const bw = Math.min(480, this.W * 0.84);
  const bh = 54;
  const padX = 13;
  const bx = this.cx - bw / 2;
  let by = startY;
  for (const ch of chips) {
    const th = WARN_THEMES[ch.theme] || WARN_THEMES.void;
    const urgent = ch.theme === "red";
    const glow = 0.4 + 0.35 * Math.sin(this.nowS * (urgent ? 8 : 4));
    c.save();
    drawWarnFrame(c, bx, by, bw, bh, CHIP_RADIUS, th, urgent, glow);

    const r = 15;
    const badgeCx = bx + padX + r;
    const badgeCy = by + bh / 2;
    drawWarnBadge(c, badgeCx, badgeCy, r, ch.icon, th, urgent);

    const textX = badgeCx + r + 13;
    c.textAlign = "left";
    c.textBaseline = "alphabetic";
    c.font = uiFont(800, "md");
    c.fillStyle = th.titleU;
    c.fillText(ch.title, textX, by + 24);
    if (ch.timer != null) {
      c.textAlign = "right";
      c.font = uiFont(800, "md");
      c.fillStyle = th.subU;
      c.fillText(`${ch.timer.toFixed(1)}s`, bx + bw - padX, by + 24);
      c.textAlign = "left";
    }
    c.font = uiFont(600, "sm");
    c.fillStyle = th.sub;
    c.fillText(ch.subtitle, textX, by + 42);
    c.restore();
    by += bh + 8;
  }
};
